import React, { useState, useEffect, useMemo, useCallback, useRef, Suspense, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  useTheme,
  useMediaQuery,
  Stack,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Button,
  ButtonGroup,
  Chip,
  Alert,
  Snackbar,
  Skeleton,
  Fade,
  Zoom,
  Tabs,
  Tab,
  Divider,
  Collapse,
  IconButton,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import HistoryIcon from '@mui/icons-material/History';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import SavingsIcon from '@mui/icons-material/Savings';
import { getUserData } from '../services/storageService';
import { getLivestreamHistory, getUserSummary, getPeriodSummaries } from '../services/livestreamService';
import { getLateIncidents } from '../services/lateIncidentService';
import { getPenalties, appealPenalty } from '../services/penaltyService';
import { getMyCommissionHistory } from '../services/commissionService';
import DiamondIcon from '@mui/icons-material/Diamond';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
const SHIFT_LABEL = {
  normal: 'Ca thường',
  sale_night: 'Ca sale / đêm',
  extra: 'Giờ phụ',
};
// Khớp PENALTY_TYPES ở backend/models/Penalty.js
const PENALTY_TYPE_LABEL = {
  fake_order: 'Buff đơn ảo',
  self_buy: 'Tự đặt đơn trên phiên của mình',
  mass_cancel: 'Đơn huỷ / hoàn hàng loạt sau khi chốt GMV',
  inflated_report: 'Khai GMV cao hơn số thật',
  fake_evidence: 'Ảnh bằng chứng giả',
  other: 'Vi phạm doanh thu',
};

// Lazy load Sidebar để giảm bundle size
const Sidebar = lazy(() => import('./common/Sidebar'));

// Cache để lưu trữ data đã fetch
const dataCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 phút

// Advanced debounce hook với cancel capability
const useAdvancedDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  const timeoutRef = useRef();

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [value, delay]);

  const cancelDebounce = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  return [debouncedValue, cancelDebounce];
};

// Intersection Observer hook cho lazy loading
const useIntersectionObserver = (options = {}) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const targetRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting);
      },
      {
        threshold: 0.1,
        rootMargin: '100px',
        ...options,
      }
    );

    const currentTarget = targetRef.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [options]);

  return [targetRef, isIntersecting];
};

// Virtual scrolling hook
const useVirtualScrolling = (items, containerHeight = 400, itemHeight = 72) => {
  const [scrollTop, setScrollTop] = useState(0);
  const containerRef = useRef();

  const visibleCount = Math.ceil(containerHeight / itemHeight) + 2;
  const startIndex = Math.floor(scrollTop / itemHeight);
  const endIndex = Math.min(startIndex + visibleCount, items.length);

  const visibleItems = useMemo(() => 
    items.slice(startIndex, endIndex).map((item, index) => ({
      ...item,
      virtualIndex: startIndex + index,
    })),
    [items, startIndex, endIndex]
  );

  const totalHeight = items.length * itemHeight;
  const offsetY = startIndex * itemHeight;

  const handleScroll = useCallback((e) => {
    setScrollTop(e.target.scrollTop);
  }, []);

  return {
    containerRef,
    visibleItems,
    totalHeight,
    offsetY,
    handleScroll,
  };
};

// Optimized table row với memo và lazy rendering
const VirtualizedTableRow = React.memo(({ record, hourlyRate, style }) => {
  const rate = record.hourlyRate != null ? record.hourlyRate : hourlyRate;
  return (
  <TableRow
    style={style}
    sx={{
      '&:hover': { bgcolor: '#f5f8fa' },
      transition: 'background-color 0.15s ease',
      height: 72,
    }}
  >
    <TableCell>
      <Stack direction="row" spacing={2} alignItems="center">
        <CalendarTodayIcon sx={{ color: '#1a237e', opacity: 0.7, fontSize: 20 }} />
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {new Date(record.date).toLocaleDateString('vi-VN', {
            weekday: 'short',
            month: 'short',
            day: 'numeric'
          })}
        </Typography>
      </Stack>
    </TableCell>
    <TableCell align="center">
      <Chip
        label={`${record.hours}h`}
        color="primary"
        variant="outlined"
        size="small"
        sx={{ fontSize: '0.75rem', height: 24 }}
      />
    </TableCell>
    <TableCell align="right">
      <Typography variant="body2" sx={{
        color: '#1a237e',
        fontWeight: 600,
      }}>
        {(record.hours * rate).toLocaleString('vi-VN')}₫
      </Typography>
    </TableCell>
    <TableCell align="center">
      <Typography variant="caption" color="text.secondary">
        {new Date(record.createdAt).toLocaleTimeString('vi-VN', {
          hour: '2-digit',
          minute: '2-digit'
        })}
      </Typography>
    </TableCell>
  </TableRow>
  );
});

// Optimized mobile card với intersection observer
const LazyMobileCard = React.memo(({ record, hourlyRate }) => {
  const [ref, isVisible] = useIntersectionObserver();
  const rate = record.hourlyRate != null ? record.hourlyRate : hourlyRate;

  if (!isVisible) {
    return (
      <Card ref={ref} sx={{ height: 80, border: '1px solid #e3f2fd', borderRadius: 2 }}>
        <Skeleton variant="rectangular" height={80} />
      </Card>
    );
  }

  return (
    <Fade in={isVisible} timeout={200}>
      <Card ref={ref} sx={{
        border: '1px solid #e3f2fd',
        borderRadius: 2,
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: '0 2px 8px rgba(26,35,126,0.15)',
        }
      }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={7}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a237e', fontSize: '0.875rem' }}>
                {new Date(record.date).toLocaleDateString('vi-VN', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric'
                })}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {new Date(record.createdAt).toLocaleTimeString('vi-VN', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </Typography>
            </Grid>
            <Grid item xs={5} sx={{ textAlign: 'right' }}>
              <Chip
                label={`${record.hours}h`}
                color="primary"
                variant="outlined"
                size="small"
                sx={{ fontSize: '0.7rem', height: 20, mb: 0.5 }}
              />
              <Typography variant="caption" color="text.secondary" display="block">
                {(record.hours * rate).toLocaleString('vi-VN')}₫
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Fade>
  );
});

// Optimized statistics card với animation
const AnimatedStatsCard = React.memo(({ title, value, subtitle, color, gradient, icon: Icon, loading }) => (
  <Zoom in={!loading} timeout={300}>
    <Card sx={{ 
      height: '100%',
      background: gradient || 'white',
      borderRadius: 3,
      boxShadow: gradient ? '0 4px 12px rgba(26,35,126,0.15)' : '0 2px 8px rgba(0,0,0,0.1)',
      borderLeft: !gradient ? `4px solid ${color}` : 'none',
    }}>
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Icon sx={{ fontSize: 24, color: gradient ? 'white' : color, opacity: 0.9 }} />
          <Typography variant="subtitle2" sx={{ ml: 1, color: gradient ? 'white' : color, fontWeight: 600 }}>
            {title}
          </Typography>
        </Box>
        {loading ? (
          <Skeleton 
            variant="text" 
            width="60%" 
            height={32} 
            sx={{ bgcolor: gradient ? 'rgba(255,255,255,0.2)' : undefined }} 
          />
        ) : (
          <Typography variant="h4" sx={{ 
            color: gradient ? 'white' : color, 
            fontWeight: 700,
            fontSize: '1.75rem'
          }}>
            {value}
          </Typography>
        )}
        <Typography variant="caption" sx={{ 
          color: gradient ? 'rgba(255,255,255,0.8)' : 'text.secondary',
          fontSize: '0.75rem'
        }}>
          {subtitle}
        </Typography>
      </CardContent>
    </Card>
  </Zoom>
));

// Optimized loading skeleton
const OptimizedSkeleton = React.memo(({ isMobile, count = 5 }) => (
  <Box sx={{ p: 2 }}>
    <Stack spacing={1}>
      {Array.from({ length: count }, (_, index) => (
        <Card key={index} sx={{ border: '1px solid #e3f2fd', borderRadius: 2 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            {isMobile ? (
              <Grid container spacing={1}>
                <Grid item xs={7}>
                  <Skeleton variant="text" width="80%" height={20} />
                  <Skeleton variant="text" width="50%" height={16} />
                </Grid>
                <Grid item xs={5} sx={{ textAlign: 'right' }}>
                  <Skeleton variant="rectangular" width={40} height={20} sx={{ borderRadius: 1, mb: 0.5, ml: 'auto' }} />
                  <Skeleton variant="text" width="60%" height={14} sx={{ ml: 'auto' }} />
                </Grid>
              </Grid>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Skeleton variant="circular" width={20} height={20} />
                <Skeleton variant="text" width="30%" height={20} />
                <Skeleton variant="rectangular" width={60} height={20} sx={{ borderRadius: 1, ml: 'auto' }} />
                <Skeleton variant="text" width="20%" height={20} />
                <Skeleton variant="text" width="15%" height={16} />
              </Box>
            )}
          </CardContent>
        </Card>
      ))}
    </Stack>
  </Box>
));

const History = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const user = getUserData();
  
  // Core state
  const [historyData, setHistoryData] = useState({
    records: [],
    pagination: {},
    summary: {}
  });
  const [userSummary, setUserSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  
  // Tab state
  const [tabValue, setTabValue] = useState(0);
  
  // Period summaries state
  const [periodSummaries, setPeriodSummaries] = useState([]);
  const [lateIncidents, setLateIncidents] = useState([]);
  const [expandedLate, setExpandedLate] = useState(null); // id kỳ đang mở chi tiết trễ
  // Quyết định xử phạt của chính mình — để tự đối chiếu khoản trừ và gửi giải trình
  const [penalties, setPenalties] = useState([]);
  const [expandedPenalty, setExpandedPenalty] = useState(null);
  const [appealDrafts, setAppealDrafts] = useState({});
  const [appealSaving, setAppealSaving] = useState(null);

  // Commission history state
  const [commHistory, setCommHistory] = useState([]);
  const [commLoading, setCommLoading] = useState(false);
  const [expandedCommMonth, setExpandedCommMonth] = useState(null);
  const [periodLoading, setPeriodLoading] = useState(false);
  
  // Filter state with optimized defaults
  const [filters, setFilters] = useState(() => ({
    page: 1,
    limit: isMobile ? 15 : 25, // Dynamic limit based on device
    startDate: null,
    endDate: null,
    sortBy: 'date',
    sortOrder: 'desc'
  }));
  
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Advanced debouncing
  const [debouncedFilters, cancelDebounce] = useAdvancedDebounce(filters, 300);

  // Cache key generation
  const getCacheKey = useCallback((filters, userId) => {
    return `history_${userId}_${JSON.stringify(filters)}`;
  }, []);

  // Memoized calculations with error handling
  const { hourlyRate, totalEarnings, avgHoursPerRecord, formattedEarnings } = useMemo(() => {
    try {
      const rate = userSummary?.hourlyRate ?? 0;
      const totalHours = userSummary?.allTime?.hours || 0;
      const totalRecords = historyData.pagination.total || 0;
      const earnings = userSummary?.allTime?.earnings ?? (totalHours * rate);
      
      // Debug logging
      console.log('💰 Calculating stats:', {
        userSummary,
        rate,
        totalHours,
        totalRecords,
        earnings,
        'historyData.pagination': historyData.pagination
      });
      
      const result = {
        hourlyRate: rate,
        totalEarnings: earnings,
        avgHoursPerRecord: totalRecords > 0 ? (totalHours / totalRecords).toFixed(1) : '0',
        formattedEarnings: earnings > 999999 
          ? `${(earnings / 1000000).toFixed(1)}M` 
          : earnings > 999 
            ? `${(earnings / 1000).toFixed(0)}K`
            : earnings.toLocaleString('vi-VN')
      };
      
      console.log('📈 Computed stats:', result);
      return result;
    } catch (error) {
      console.error('Error calculating stats:', error);
      return {
        hourlyRate: 0,
        totalEarnings: 0,
        avgHoursPerRecord: '0',
        formattedEarnings: '0'
      };
    }
  }, [userSummary, historyData.pagination]);

  // Memoized filtered statistics - THÊM MỚI
  const filteredStats = useMemo(() => {
    try {
      const records = historyData.records || [];
      const filteredHours = records.reduce((sum, record) => sum + (record.hours || 0), 0);
      const filteredEarnings = filteredHours * hourlyRate;
      const filteredCount = records.length;
      
      // Format earnings for display
      const formattedFilteredEarnings = filteredEarnings > 999999 
        ? `${(filteredEarnings / 1000000).toFixed(1)}M` 
        : filteredEarnings > 999 
          ? `${(filteredEarnings / 1000).toFixed(0)}K`
          : filteredEarnings.toLocaleString('vi-VN');

      // Check if filtered results are different from total
      const hasFilter = filters.startDate || filters.endDate;
      // Luôn hiển thị khi có filter, không cần so sánh với total
      const isFiltered = hasFilter;

      console.log('🔍 Filtered stats DEBUG:', {
        'historyData.records.length': records.length,
        'historyData.pagination': historyData.pagination,
        'filters.startDate': filters.startDate,
        'filters.endDate': filters.endDate,
        filteredHours,
        filteredEarnings,
        filteredCount,
        hasFilter,
        isFiltered,
        hourlyRate,
        'sample records': records.slice(0, 3)
      });

      return {
        hours: filteredHours,
        earnings: filteredEarnings,
        count: filteredCount,
        avgHours: filteredCount > 0 ? (filteredHours / filteredCount).toFixed(1) : '0',
        formattedEarnings: formattedFilteredEarnings,
        isFiltered,
        hasFilter
      };
    } catch (error) {
      console.error('Error calculating filtered stats:', error);
      return {
        hours: 0,
        earnings: 0,
        count: 0,
        avgHours: '0',
        formattedEarnings: '0',
        isFiltered: false,
        hasFilter: false
      };
    }
  }, [historyData.records, hourlyRate, filters.startDate, filters.endDate, historyData.pagination]);

  // Optimized notification handler
  const showNotification = useCallback((message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  }, []);

  const handleCloseNotification = useCallback(() => {
    setNotification(prev => ({ ...prev, open: false }));
  }, []);

  // Optimized user summary fetching
  const fetchUserSummary = useCallback(async () => {
    const cacheKey = `userSummary_${user.userId}`;
    const cached = dataCache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      setUserSummary(cached.data);
      return;
    }

    try {
      const result = await getUserSummary(user.userId);
      console.log('👤 getUserSummary result:', result); // Debug log
      if (result.success) {
        console.log('✅ UserSummary data:', result.data); // Debug log
        setUserSummary(result.data);
        dataCache.set(cacheKey, {
          data: result.data,
          timestamp: Date.now()
        });
      } else {
        console.error('❌ getUserSummary failed:', result);
      }
    } catch (error) {
      console.error('🚨 Error fetching user summary:', error);
    }
  }, [user.userId]);

  // Fetch period summaries
  const fetchPeriodSummaries = useCallback(async () => {
    const cacheKey = `periodSummaries_${user.userId}`;
    const cached = dataCache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      setPeriodSummaries(cached.data);
      return;
    }

    setPeriodLoading(true);
    try {
      // Nạp song song lịch sử đi live trễ để hiện chi tiết từng lần dưới mỗi kỳ.
      // Dữ liệu nhỏ nên lấy hết một lần rồi nhóm theo kỳ khi render.
      const [result, late, penaltyRes] = await Promise.all([
        getPeriodSummaries('all', user.userId),
        getLateIncidents({ userId: user.userId }),
        getPenalties({ userId: user.userId }),
      ]);
      setLateIncidents(late.success ? (late.data || []) : []);
      setPenalties(penaltyRes.success ? (penaltyRes.data || []) : []);

      if (result.success) {
        setPeriodSummaries(result.data.data || []);
        dataCache.set(cacheKey, {
          data: result.data.data || [],
          timestamp: Date.now()
        });
      } else {
        console.error('❌ getPeriodSummaries failed:', result);
        showNotification('Không thể lấy lịch sử thu nhập các kỳ', 'error');
      }
    } catch (error) {
      console.error('🚨 Error fetching period summaries:', error);
      showNotification('Có lỗi xảy ra khi lấy lịch sử thu nhập', 'error');
    } finally {
      setPeriodLoading(false);
    }
  }, [user.userId, showNotification]);

  // Advanced data fetching with caching
  const fetchData = useCallback(async (currentFilters = filters, isInitial = false) => {
    const cacheKey = getCacheKey(currentFilters, user.userId);
    const cached = dataCache.get(cacheKey);
    
    // Check cache validity
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      setHistoryData(cached.data);
      setInitialLoading(false);
      setLoading(false);
      return;
    }

    if (isInitial) {
      setInitialLoading(true);
    } else {
      setLoading(true);
    }

    try {
      const options = {
        page: currentFilters.page,
        limit: currentFilters.limit,
        sortBy: currentFilters.sortBy,
        sortOrder: currentFilters.sortOrder
      };
      
      if (currentFilters.startDate) {
        options.startDate = currentFilters.startDate.toISOString();
      }
      if (currentFilters.endDate) {
        options.endDate = currentFilters.endDate.toISOString();
      }
      
      const result = await getLivestreamHistory(user.userId, options);
      console.log('📊 getLivestreamHistory result:', result); // Debug log
      
      if (result.success) {
        console.log('✅ History data:', result.data); // Debug log
        setHistoryData(result.data);
        
        // Cache the result
        dataCache.set(cacheKey, {
          data: result.data,
          timestamp: Date.now()
        });
        
        // Clean old cache entries
        if (dataCache.size > 50) {
          const oldestKey = dataCache.keys().next().value;
          dataCache.delete(oldestKey);
        }
      } else {
        console.error('❌ getLivestreamHistory failed:', result);
        showNotification('Không thể lấy lịch sử chấm công', 'error');
      }
    } catch (error) {
      console.error('🚨 Error fetching history:', error);
      showNotification('Có lỗi xảy ra khi lấy dữ liệu', 'error');
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [user.userId, showNotification, getCacheKey]);

  // Effect hooks với cleanup
  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    
    const controller = new AbortController();
    
    Promise.all([
      fetchData(debouncedFilters, true),
      fetchUserSummary(),
      fetchPeriodSummaries()
    ]);

    return () => {
      controller.abort();
      cancelDebounce();
    };
  }, [user, navigate, fetchUserSummary, fetchPeriodSummaries]);

  useEffect(() => {
    if (!initialLoading && user) {
      fetchData(debouncedFilters);
    }
  }, [debouncedFilters, fetchData, initialLoading, user]);

  // Optimized event handlers
  const handlePageChange = useCallback((event, newPage) => {
    setFilters(prev => ({ ...prev, page: newPage + 1 }));
  }, []);

  const handleRowsPerPageChange = useCallback((event) => {
    const newLimit = parseInt(event.target.value, 10);
    setFilters(prev => ({ ...prev, limit: newLimit, page: 1 }));
  }, []);

  const handleApplyFilters = useCallback(() => {
    setFilters(prev => ({ ...prev, page: 1 }));
  }, []);

  const handleResetFilters = useCallback(() => {
    const resetFilters = {
      page: 1,
      limit: isMobile ? 15 : 25,
      startDate: null,
      endDate: null,
      sortBy: 'date',
      sortOrder: 'desc'
    };
    setFilters(resetFilters);
    
    // Clear relevant cache
    const keysToDelete = [];
    for (const key of dataCache.keys()) {
      if (key.includes(user.userId)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => dataCache.delete(key));
  }, [isMobile, user.userId]);

  const handleRefresh = useCallback(() => {
    // Clear cache
    dataCache.clear();
    fetchData(filters, true);
    fetchUserSummary();
    fetchPeriodSummaries();
    showNotification('Đã cập nhật dữ liệu');
  }, [fetchData, fetchUserSummary, fetchPeriodSummaries, filters, showNotification]);

  // Memoized filter handlers
  const filterHandlers = useMemo(() => ({
    startDate: (date) => setFilters(prev => ({ ...prev, startDate: date, page: 1 })),
    endDate: (date) => setFilters(prev => ({ ...prev, endDate: date, page: 1 })),
    sortBy: (e) => setFilters(prev => ({ ...prev, sortBy: e.target.value, page: 1 })),
    sortOrder: (e) => setFilters(prev => ({ ...prev, sortOrder: e.target.value, page: 1 }))
  }), []);

  const fetchCommHistory = useCallback(async () => {
    setCommLoading(true);
    try {
      const result = await getMyCommissionHistory(24);
      if (result.success) setCommHistory(result.data);
    } catch (err) {
      console.error('Error fetching commission history:', err);
    }
    setCommLoading(false);
  }, []);

  // Tab change handler
  const handleTabChange = useCallback((event, newValue) => {
    setTabValue(newValue);
    if (newValue === 2 && commHistory.length === 0) {
      fetchCommHistory();
    }
  }, [commHistory.length, fetchCommHistory]);

  // Period summary card component
  const PeriodSummaryCard = React.memo(({ summary, index }) => {
    const fmt = (n) => new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 }).format(n || 0);
    const auto = summary.autoBonuses || [];
    const manual = summary.bonuses || [];
    const deds = summary.deductions || [];
    const cut = summary.cutBonuses || [];
    const lb = summary.leaderBonusBreakdown || {};
    const grand = summary.grandTotal ?? summary.totalEarnings;
    const Line = ({ label, value, color = 'text.primary', sub, bold = false }) => (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
          <Typography variant="body2" sx={{ color, fontWeight: bold ? 700 : 400 }}>{label}</Typography>
          <Typography variant="body2" sx={{ color, fontWeight: bold ? 700 : 600, whiteSpace: 'nowrap' }}>{value}</Typography>
        </Box>
        {sub && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>{sub}</Typography>}
      </Box>
    );
    return (
    <Zoom in timeout={300 + index * 100}>
        <Card sx={{ 
        border: '1px solid #e3f2fd',
        borderRadius: 2,
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        '&:hover': { 
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 12px rgba(26,35,126,0.15)',
        }
      }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SavingsIcon sx={{ color: '#1a237e', mr: 1, fontSize: 24 }} />
                <Typography variant="h6" sx={{ color: '#1a237e', fontWeight: 600 }}>
                  Kỳ {new Date(summary.periodStart).toLocaleDateString('vi-VN')} - {new Date(summary.periodEnd).toLocaleDateString('vi-VN')}
                </Typography>
              </Box>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Thời gian:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {Math.ceil((new Date(summary.periodEnd) - new Date(summary.periodStart)) / (1000 * 60 * 60 * 24))} ngày
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Tỷ lệ/giờ:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {new Intl.NumberFormat('vi-VN', {
                      style: 'currency',
                      currency: 'VND'
                    }).format(summary.hourlyRate)}
                  </Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack spacing={2}>
                <Paper sx={{ p: 2, bgcolor: '#f5f8fa', border: '1px solid #e3f2fd', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AccessTimeIcon sx={{ color: '#1a237e', mr: 1, fontSize: 20 }} />
                    <Typography variant="body2" color="text.secondary">
                      Tổng giờ làm việc
                    </Typography>
                  </Box>
                  <Typography variant="h5" sx={{ color: '#1a237e', fontWeight: 700 }}>
                    {summary.totalHours} giờ
                  </Typography>
                </Paper>
                <Paper sx={{ p: 2, bgcolor: '#f5f8fa', border: '1px solid #e3f2fd', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <MonetizationOnIcon sx={{ color: '#1a237e', mr: 1, fontSize: 20 }} />
                    <Typography variant="body2" color="text.secondary">
                      Thực nhận kỳ này
                    </Typography>
                  </Box>
                  <Typography variant="h5" sx={{ color: '#1a237e', fontWeight: 700 }}>
                    {fmt(grand)}
                  </Typography>
                </Paper>
              </Stack>
            </Grid>
          </Grid>

          {/* Chi tiết thu nhập theo chính sách */}
          <Divider sx={{ my: 2 }}>
            <Chip label="Chi tiết thu nhập" size="small" color="primary" variant="outlined" sx={{ fontSize: '0.7rem' }} />
          </Divider>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fafcff' }}>
            <Stack spacing={0.75} divider={<Divider flexItem sx={{ opacity: 0.5 }} />}>
              {summary.baseSalary > 0 && <Line label="Lương cứng" value={fmt(summary.baseSalary)} />}
              {summary.liveEarnings > 0 && <Line label="Thu nhập giờ live" value={fmt(summary.liveEarnings)} />}
              {summary.commissionAmount > 0 && (
                <Line
                  label={`Hoa hồng${summary.effectiveCommissionRate ? ` (${summary.effectiveCommissionRate}%)` : ''}`}
                  value={fmt(summary.commissionAmount)}
                  sub={`GMV ${fmt(summary.totalGMV)} — Shopee ${fmt(summary.gmvShopee)} · TikTok ${fmt(summary.gmvTiktok)}`}
                />
              )}
              {summary.leaderBonus > 0 && (
                <Line
                  label="Thưởng quản lý (leader)"
                  value={fmt(summary.leaderBonus)}
                  sub={[
                    lb.teamCommission ? `HH team ${fmt(lb.teamCommission)}` : null,
                    lb.growthBonus ? `Tăng trưởng ${fmt(lb.growthBonus)}` : null,
                    lb.tiktokKpiBonus ? `KPI TikTok ${fmt(lb.tiktokKpiBonus)}` : null,
                    lb.tiktokRatioBonus ? `Tỷ trọng TikTok ${fmt(lb.tiktokRatioBonus)}` : null,
                  ].filter(Boolean).join(' · ') || undefined}
                />
              )}
              {auto.map((b, i) => (
                <Line key={`a${i}`} label={`+ ${b.reason || 'Thưởng chính sách'}`} value={`+ ${fmt(b.amount)}`} color="success.main" />
              ))}
              {manual.map((b, i) => (
                <Line key={`m${i}`} label={`+ Thưởng thêm${b.reason ? `: ${b.reason}` : ''}`} value={`+ ${fmt(b.amount)}`} color="#f57c00" />
              ))}
              {cut.map((b, i) => (
                <Line
                  key={`c${i}`}
                  label={`✗ ${b.reason || 'Thưởng bị cắt'}`}
                  value="0"
                  sub={`Đáng lẽ được ${fmt(b.amount)} — bị cắt do: ${b.cutBy || 'quyết định của quản lý'}`}
                  color="#9e9e9e"
                />
              ))}
              {deds.map((d, i) => (
                <React.Fragment key={`d${i}`}>
                  <Line label={`− ${d.reason || 'Khấu trừ'}${d.auto === false ? ' (thủ công)' : ''}`} value={`− ${fmt(d.amount)}`} color="error.main" />
                  {/* Tách khoản trừ thành từng dòng — một con số gộp thì không ai biết tiền đi đâu */}
                  {(d.breakdown || []).length > 0 && (
                    <Box sx={{ pl: 2, pb: 0.5, borderLeft: '2px solid #fecaca', ml: 0.5 }}>
                      {d.breakdown.map((b, j) => (
                        <Box key={j} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, py: 0.25 }}>
                          <Typography variant="caption" color="text.secondary">{b.label}</Typography>
                          <Typography variant="caption" color="error.main" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                            − {fmt(b.amount)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </React.Fragment>
              ))}
              <Line label="Thực nhận" value={fmt(grand)} bold color="#1a237e" />
            </Stack>

            {/* Admin đã sửa tay số liệu tháng này — phải nói rõ sửa gì và vì sao, nếu không
                nhân sự chỉ thấy hoa hồng tụt mà không hiểu chuyện gì xảy ra. */}
            {summary.commissionAdjustment && (() => {
              const ca = summary.commissionAdjustment;
              const gmvChanged = ca.targetGMV != null && ca.targetGMV !== ca.originalGMV;
              const comChanged = ca.commissionOverride != null;
              return (
                <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: '#fffbeb', border: '1px solid #fde68a' }}>
                  <Typography variant="caption" fontWeight={700} color="#92400e" sx={{ display: 'block', mb: 0.5 }}>
                    Quản lý đã điều chỉnh số liệu kỳ này
                  </Typography>
                  {gmvChanged && (
                    <Typography variant="caption" color="#7c4a03" sx={{ display: 'block' }}>
                      • Doanh thu: {fmt(ca.originalGMV)} → <b>{fmt(ca.targetGMV)}</b>
                      {ca.targetGMV < ca.originalGMV
                        ? ` (giảm ${fmt(ca.originalGMV - ca.targetGMV)}, kéo theo hoa hồng và mốc thưởng GMV giảm)`
                        : ` (tăng ${fmt(ca.targetGMV - ca.originalGMV)})`}
                    </Typography>
                  )}
                  {comChanged && (
                    <Typography variant="caption" color="#7c4a03" sx={{ display: 'block' }}>
                      • Hoa hồng: hệ thống tính {fmt(ca.originalCommission)} → chốt <b>{fmt(ca.commissionOverride)}</b>
                    </Typography>
                  )}
                  {!!ca.reason && (
                    <Typography variant="caption" color="#7c4a03" sx={{ display: 'block', mt: 0.5 }}>
                      <b>Lý do:</b> {ca.reason}
                    </Typography>
                  )}
                  <Typography variant="caption" color="#a0763a" sx={{ display: 'block', mt: 0.5 }}>
                    {ca.byName ? `Người điều chỉnh: ${ca.byName}. ` : ''}
                    {ca.at ? `Thời điểm: ${new Date(ca.at).toLocaleString('vi-VN')}. ` : ''}
                    Có thắc mắc, liên hệ quản lý để được giải thích.
                  </Typography>
                </Box>
              );
            })()}

            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
              <Chip size="small" variant="outlined" label={`KPI: ${summary.kpiHours ?? summary.totalHours}h`} />
              {typeof summary.minHoursMet === 'boolean' && (
                <Chip size="small" color={summary.minHoursMet ? 'success' : 'warning'} variant={summary.minHoursMet ? 'outlined' : 'filled'}
                  label={summary.minHoursMet ? 'Đủ giờ tối thiểu' : 'Chưa đủ giờ tối thiểu'} />
              )}
              {summary.nightShiftCount > 0 && <Chip size="small" variant="outlined" label={`Ca đêm: ${summary.nightShiftCount}`} />}
              {summary.lateCount > 0 && (
                <Chip size="small" color="error" variant={expandedLate === summary._id ? 'outlined' : 'filled'}
                  onClick={() => setExpandedLate(expandedLate === summary._id ? null : summary._id)}
                  label={`Trễ: ${summary.lateCount} lần${summary.lateMultiplier > 1 ? ` (hệ số ×${summary.lateMultiplier})` : ''} — xem chi tiết`}
                  sx={{ cursor: 'pointer' }} />
              )}
              {summary.penaltyCount > 0 && (
                <Chip size="small" color="error" variant={expandedPenalty === summary._id ? 'outlined' : 'filled'}
                  onClick={() => setExpandedPenalty(expandedPenalty === summary._id ? null : summary._id)}
                  label={`Bị xử phạt: ${summary.penaltyCount} quyết định${summary.penaltyFakeGMV > 0 ? ` — đã loại ${fmt(summary.penaltyFakeGMV)} GMV ảo` : ''} — xem chi tiết`}
                  sx={{ cursor: 'pointer', maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }} />
              )}
              {summary.teamLateStats?.totalShifts > 0 && (
                <Chip size="small" variant="outlined"
                  label={`Tỷ lệ trễ team: ${summary.teamLateStats.ratePct}% (${summary.teamLateStats.lateCount}/${summary.teamLateStats.totalShifts})`} />
              )}
              {(summary.warnings || []).map((w, i) => (
                <Chip key={`w${i}`} size="small" color="warning" variant="outlined" label={w}
                  sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }} />
              ))}
            </Stack>

            {/* Chi tiết từng lần trễ của kỳ này — để tự đối chiếu khoản khấu trừ */}
            {expandedLate === summary._id && (() => {
              const ps = new Date(summary.periodStart), pe = new Date(summary.periodEnd);
              const mine = (lateIncidents || [])
                .filter(li => { const d = new Date(li.date); return d >= ps && d <= pe; })
                .sort((a, b) => new Date(a.date) - new Date(b.date));
              return (
                <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: '#fff8e1', border: '1px solid #ffe0b2' }}>
                  <Typography variant="caption" fontWeight={700} color="#b45309" sx={{ display: 'block', mb: 0.5 }}>
                    Chi tiết {mine.length} lần đi live trễ
                  </Typography>
                  {mine.length === 0 && (
                    <Typography variant="caption" color="text.secondary">Không tải được chi tiết — thử tải lại trang</Typography>
                  )}
                  {mine.map(li => {
                    const excused = li.status === 'excused';
                    return (
                      <Box key={li._id} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', py: 0.6, borderTop: '1px solid #ffe9c4' }}>
                        <Typography variant="caption" fontWeight={800} color="#b45309" sx={{ minWidth: 44 }}>
                          {new Date(li.date).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' })}
                        </Typography>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="caption" fontWeight={600} color="#7c4a03" sx={{ display: 'block' }}>
                            {SHIFT_LABEL[li.shiftType] || li.shiftType}
                            {li.platform ? ` · ${li.platform === 'tiktok' ? 'TikTok' : li.platform === 'shopee' ? 'Shopee' : 'Khác'}` : ''}
                          </Typography>
                          {(li.scheduledStartTime || li.actualStartTime) && (
                            <Typography variant="caption" color="#a0763a" sx={{ display: 'block' }}>
                              Giờ ca {li.scheduledStartTime || '—'} → lên sóng {li.actualStartTime || '—'}
                            </Typography>
                          )}
                          {excused && !!li.excuseReason && (
                            <Typography variant="caption" color="#a0763a" sx={{ display: 'block' }}>Miễn phạt: {li.excuseReason}</Typography>
                          )}
                          {!!li.note && <Typography variant="caption" color="#a0763a" sx={{ display: 'block' }}>{li.note}</Typography>}
                        </Box>
                        <Chip size="small" color={excused ? 'success' : 'error'} variant={excused ? 'outlined' : 'filled'}
                          label={excused ? 'miễn' : 'tính phạt'} sx={{ height: 20, fontSize: '0.65rem' }} />
                      </Box>
                    );
                  })}
                </Box>
              );
            })()}

            {/* Chi tiết quyết định xử phạt của kỳ này + ô gửi giải trình.
                Bị trừ tiền thì phải xem được căn cứ và phản hồi ngay tại phiếu lương. */}
            {expandedPenalty === summary._id && (() => {
              const ps = new Date(summary.periodStart), pe = new Date(summary.periodEnd);
              const mine = (penalties || [])
                .filter(pn => pn.status === 'active')
                // Trả góp: quyết định của kỳ trước vẫn còn trừ ở kỳ này nên không lọc cứng theo kỳ
                .filter(pn => {
                  const d = new Date(pn.periodDate);
                  if (d > pe) return false;
                  const offset = (ps.getFullYear() - d.getFullYear()) * 12 + (ps.getMonth() - d.getMonth());
                  return offset >= 0 && offset < Math.max(1, pn.installments || 1);
                })
                .sort((a, b) => new Date(a.periodDate) - new Date(b.periodDate));
              const submitAppeal = async (id) => {
                const note = (appealDrafts[id] || '').trim();
                if (!note) return showNotification('Nhập nội dung giải trình', 'warning');
                setAppealSaving(id);
                const res = await appealPenalty(id, note);
                setAppealSaving(null);
                if (res.success) {
                  showNotification('Đã gửi giải trình cho quản lý', 'success');
                  setPenalties(prev => prev.map(x => x._id === id ? { ...x, appealNote: note, appealedAt: new Date() } : x));
                } else {
                  showNotification(res.error?.message || 'Không gửi được giải trình', 'error');
                }
              };
              const measuresOf = (pn) => [
                pn.retroTotal > 0 ? `thu hồi ${fmt(pn.retroTotal)} của các tháng đã trả lương` : null,
                pn.voidSession ? 'huỷ toàn bộ doanh thu phiên' : (pn.fakeGMV > 0 ? `loại ${fmt(pn.fakeGMV)} GMV ảo` : null),
                pn.gmvPenaltyPercent > 0 ? `trừ ${pn.gmvPenaltyPercent}% GMV kỳ` : null,
                pn.fixedAmount > 0 ? `phạt ${fmt(pn.fixedAmount)}` : null,
                pn.cutGmvBonus ? 'cắt thưởng mốc GMV' : null,
                pn.cutAttendanceBonus ? 'cắt thưởng chuyên cần' : null,
                pn.cutNightAllowance ? 'cắt phụ cấp ca đêm' : null,
              ].filter(Boolean);
              return (
                <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: '#fef2f2', border: '1px solid #fecaca' }}>
                  <Typography variant="caption" fontWeight={700} color="#b91c1c" sx={{ display: 'block', mb: 0.5 }}>
                    Chi tiết {mine.length} quyết định xử phạt
                  </Typography>
                  {mine.length === 0 && (
                    <Typography variant="caption" color="text.secondary">Không tải được chi tiết — thử tải lại trang</Typography>
                  )}
                  {mine.map(pn => (
                    <Box key={pn._id} sx={{ py: 0.8, borderTop: '1px solid #fecaca' }}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                        <Typography variant="caption" fontWeight={800} color="#b91c1c" sx={{ minWidth: 44 }}>
                          {new Date(pn.periodDate).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' })}
                        </Typography>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="caption" fontWeight={600} color="#7f1d1d" sx={{ display: 'block' }}>
                            {PENALTY_TYPE_LABEL[pn.type] || 'Vi phạm doanh thu'}
                            {(pn.violationMonths || []).length
                              ? ` — vi phạm ${pn.violationMonths.map(m => `T${Number(m.slice(5))}`).join(', ')}/${pn.violationMonths[0].slice(0, 4)}`
                              : ''}
                          </Typography>
                          {!!pn.description && (
                            <Typography variant="caption" color="#9a3d3d" sx={{ display: 'block' }}>{pn.description}</Typography>
                          )}
                          <Typography variant="caption" color="#9a3d3d" sx={{ display: 'block' }}>
                            Biện pháp: {measuresOf(pn).join(' · ')}
                          </Typography>
                          {pn.installments > 1 && (
                            <Typography variant="caption" color="#9a3d3d" sx={{ display: 'block' }}>
                              Trả góp {fmt(pn.appliedSnapshot?.perPeriod)}/kỳ × {pn.installments} kỳ
                              (tổng {fmt(pn.appliedSnapshot?.total)})
                            </Typography>
                          )}
                        </Box>
                        {pn.appliedSnapshot?.total > 0 && (
                          <Typography variant="caption" fontWeight={800} color="#b91c1c" sx={{ whiteSpace: 'nowrap' }}>
                            − {fmt(pn.appliedSnapshot.total)}
                          </Typography>
                        )}
                      </Box>

                      {pn.appealedAt ? (
                        <Typography variant="caption" color="#0369a1" sx={{ display: 'block', mt: 0.5 }}>
                          Đã gửi giải trình {new Date(pn.appealedAt).toLocaleDateString('vi-VN')}: {pn.appealNote}
                        </Typography>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.75, alignItems: 'flex-start' }}>
                          <TextField
                            size="small" fullWidth multiline maxRows={3}
                            placeholder="Không đồng ý? Gửi giải trình kèm căn cứ cho quản lý…"
                            value={appealDrafts[pn._id] || ''}
                            onChange={(e) => setAppealDrafts(prev => ({ ...prev, [pn._id]: e.target.value }))}
                            sx={{ bgcolor: 'white', borderRadius: 1 }}
                          />
                          <Button size="small" variant="contained" color="error" disabled={appealSaving === pn._id}
                            onClick={() => submitAppeal(pn._id)} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                            Gửi
                          </Button>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              );
            })()}

          </Paper>

          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Lưu lúc: {new Date(summary.createdAt).toLocaleString('vi-VN')}
            </Typography>
            <Chip
              label="Đã lưu"
              color="success"
              variant="outlined"
              size="small"
              sx={{ fontSize: '0.7rem' }}
            />
          </Box>
        </CardContent>
      </Card>
    </Zoom>
    );
  });

  // Virtual scrolling for large datasets
  const {
    containerRef,
    visibleItems,
    totalHeight,
    offsetY,
    handleScroll
  } = useVirtualScrolling(historyData.records, 400, 72);

  // Memoized rendered content
  const renderedContent = useMemo(() => {
    if (!historyData.records.length) return null;

    if (isMobile) {
      return (
        <Box sx={{ p: 1 }}>
          <Stack spacing={1}>
            {historyData.records.map((record) => (
              <LazyMobileCard 
                key={record._id} 
                record={record} 
                hourlyRate={hourlyRate}
              />
            ))}
          </Stack>
        </Box>
      );
    }

    // Use virtual scrolling for desktop when more than 20 items
    if (historyData.records.length > 20) {
      return (
        <Box 
          ref={containerRef}
          onScroll={handleScroll}
          sx={{ height: 400, overflow: 'auto' }}
        >
          <Box sx={{ height: totalHeight, position: 'relative' }}>
            <Box sx={{ transform: `translateY(${offsetY}px)` }}>
            <Table>
                <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: '#1a237e' }}>Ngày</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: '#1a237e' }}>Giờ</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#1a237e' }}>Thu nhập</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: '#1a237e' }}>Thời gian</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                  {visibleItems.map((record) => (
                    <VirtualizedTableRow 
                      key={record._id}
                      record={record} 
                      hourlyRate={hourlyRate}
                    />
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Box>
        </Box>
      );
    }

    // Regular table for smaller datasets
    return (
      <TableContainer>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, color: '#1a237e' }}>Ngày</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, color: '#1a237e' }}>Giờ</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, color: '#1a237e' }}>Thu nhập</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, color: '#1a237e' }}>Thời gian</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {historyData.records.map((record) => (
              <VirtualizedTableRow 
                key={record._id} 
                record={record} 
                hourlyRate={hourlyRate}
              />
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
    );
  }, [historyData.records, isMobile, hourlyRate, visibleItems, totalHeight, offsetY, handleScroll, containerRef]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><Skeleton variant="rectangular" width="100%" height={200} /></Box>}>
        <Sidebar user={user}>
          <Container maxWidth="xl">
            {/* Optimized Header */}
            <Fade in timeout={500}>
              <Box sx={{ mb: 3 }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                  <HistoryIcon sx={{ color: '#1a237e', fontSize: 28 }} />
                  <Typography variant="h5" sx={{ color: '#1a237e', fontWeight: 700 }}>
                    Lịch sử làm việc
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Xem lại toàn bộ lịch sử chấm công và thu nhập theo kỳ với các bộ lọc và thống kê chi tiết
                </Typography>
              </Box>
            </Fade>

            {/* Navigation Tabs */}
            <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  aria-label="lịch sử tabs"
                  sx={{
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      minHeight: 48,
                    },
                    '& .Mui-selected': {
                      color: '#1a237e',
                    },
                  }}
                >
                  <Tab 
                    icon={<HistoryIcon />} 
                    iconPosition="start"
                    label="Lịch sử chấm công" 
                    sx={{ color: '#1a237e' }}
                  />
                  <Tab 
                    icon={<WorkHistoryIcon />} 
                    iconPosition="start"
                    label="Thu nhập theo kỳ" 
                    sx={{ color: '#1a237e' }}
                  />
                  <Tab 
                    icon={<DiamondIcon />} 
                    iconPosition="start"
                    label="Hoa hồng" 
                    sx={{ color: '#d97706' }}
                  />
                </Tabs>
              </Box>
            </Card>

            {/* Tab Content */}
            {tabValue === 0 && (
              <>
                {/* Optimized Statistics Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} sm={3}>
                    <AnimatedStatsCard
                      title="Tổng giờ"
                      value={!userSummary ? 'Loading...' : (userSummary?.allTime?.hours || 0)}
                      subtitle="Giờ làm việc"
                      color="#1a237e"
                      gradient="linear-gradient(45deg, #1a237e 30%, #534bae 90%)"
                      icon={AccessTimeIcon}
                      loading={!userSummary && initialLoading}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <AnimatedStatsCard
                      title="Số lần"
                      value={!historyData.pagination ? 'Loading...' : (historyData.pagination.total || 0)}
                      subtitle="Lần chấm công"
                      color="#4caf50"
                      icon={CalendarTodayIcon}
                      loading={!historyData.pagination && initialLoading}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <AnimatedStatsCard
                      title="Thu nhập"
                      value={!userSummary ? 'Loading...' : `${formattedEarnings}₫`}
                      subtitle="Tổng thu nhập"
                      color="#ff9800"
                      icon={MonetizationOnIcon}
                      loading={!userSummary && initialLoading}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <AnimatedStatsCard
                      title="Trung bình"
                      value={(!userSummary || !historyData.pagination) ? 'Loading...' : avgHoursPerRecord}
                      subtitle="Giờ/lần"
                      color="#2196f3"
                      icon={TrendingUpIcon}
                      loading={(!userSummary || !historyData.pagination) && initialLoading}
                    />
                  </Grid>
                </Grid>

                {/* Filtered Statistics - THÊM MỚI */}
                {filteredStats.isFiltered && (
                  <Fade in timeout={600}>
                    <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', border: '2px solid #e3f2fd' }}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: 'linear-gradient(135deg, #e3f2fd 0%, #f5f8fa 100%)',
                        borderBottom: '1px solid #e3f2fd'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <FilterListIcon sx={{ color: '#1a237e', mr: 1, fontSize: 24 }} />
                          <Typography variant="h6" sx={{ color: '#1a237e', fontWeight: 600 }}>
                            Thống kê khoảng thời gian đã chọn
                          </Typography>
                        </Box>
                        {filters.startDate && filters.endDate && (
                          <Typography variant="body2" color="text.secondary">
                            📅 Từ {filters.startDate.toLocaleDateString('vi-VN')} đến {filters.endDate.toLocaleDateString('vi-VN')}
                          </Typography>
                        )}
                        {(filters.startDate && !filters.endDate) && (
                          <Typography variant="body2" color="text.secondary">
                            📅 Từ {filters.startDate.toLocaleDateString('vi-VN')} đến hiện tại
                          </Typography>
                        )}
                        {(!filters.startDate && filters.endDate) && (
                          <Typography variant="body2" color="text.secondary">
                            📅 Từ đầu đến {filters.endDate.toLocaleDateString('vi-VN')}
                          </Typography>
                        )}
                        
                      </Box>
                      <CardContent sx={{ p: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={6} sm={3}>
                            <Paper sx={{ 
                              p: 2, 
                              textAlign: 'center', 
                              bgcolor: '#f5f8fa', 
                              border: '1px solid #e3f2fd',
                              borderRadius: 2,
                              transition: 'transform 0.2s ease',
                              '&:hover': { transform: 'translateY(-2px)' }
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                                <AccessTimeIcon sx={{ color: '#1a237e', mr: 1, fontSize: 20 }} />
                                <Typography variant="subtitle2" sx={{ color: '#1a237e', fontWeight: 600 }}>
                                  Tổng giờ
                                </Typography>
                              </Box>
                              <Typography variant="h4" sx={{ color: '#1a237e', fontWeight: 700, fontSize: '1.75rem' }}>
                                {filteredStats.hours}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                giờ làm việc
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Paper sx={{ 
                              p: 2, 
                              textAlign: 'center', 
                              bgcolor: '#f5f8fa', 
                              border: '1px solid #e3f2fd',
                              borderRadius: 2,
                              transition: 'transform 0.2s ease',
                              '&:hover': { transform: 'translateY(-2px)' }
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                                <MonetizationOnIcon sx={{ color: '#ff9800', mr: 1, fontSize: 20 }} />
                                <Typography variant="subtitle2" sx={{ color: '#ff9800', fontWeight: 600 }}>
                                  Thu nhập
                                </Typography>
                              </Box>
                              <Typography variant="h4" sx={{ color: '#ff9800', fontWeight: 700, fontSize: '1.75rem' }}>
                                {filteredStats.formattedEarnings}₫
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {new Intl.NumberFormat('vi-VN', {
                                  style: 'currency',
                                  currency: 'VND'
                                }).format(filteredStats.earnings)}
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Paper sx={{ 
                              p: 2, 
                              textAlign: 'center', 
                              bgcolor: '#f5f8fa', 
                              border: '1px solid #e3f2fd',
                              borderRadius: 2,
                              transition: 'transform 0.2s ease',
                              '&:hover': { transform: 'translateY(-2px)' }
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                                <CalendarTodayIcon sx={{ color: '#4caf50', mr: 1, fontSize: 20 }} />
                                <Typography variant="subtitle2" sx={{ color: '#4caf50', fontWeight: 600 }}>
                                  Số lần
                                </Typography>
                              </Box>
                              <Typography variant="h4" sx={{ color: '#4caf50', fontWeight: 700, fontSize: '1.75rem' }}>
                                {filteredStats.count}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                lần chấm công
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Paper sx={{ 
                              p: 2, 
                              textAlign: 'center', 
                              bgcolor: '#f5f8fa', 
                              border: '1px solid #e3f2fd',
                              borderRadius: 2,
                              transition: 'transform 0.2s ease',
                              '&:hover': { transform: 'translateY(-2px)' }
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                                <TrendingUpIcon sx={{ color: '#2196f3', mr: 1, fontSize: 20 }} />
                                <Typography variant="subtitle2" sx={{ color: '#2196f3', fontWeight: 600 }}>
                                  Trung bình
                                </Typography>
                              </Box>
                              <Typography variant="h4" sx={{ color: '#2196f3', fontWeight: 700, fontSize: '1.75rem' }}>
                                {filteredStats.avgHours}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                giờ/lần
                              </Typography>
                            </Paper>
                          </Grid>
                        </Grid>
                        
                        {/* Additional info */}
                        <Box sx={{ mt: 2, p: 1.5, bgcolor: '#fff3e0', borderRadius: 2, border: '1px solid #ffcc02' }}>
                          <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 500, textAlign: 'center' }}>
                            💡 <strong>Kết quả lọc:</strong> {filteredStats.count} bản ghi được hiển thị
                            {historyData.pagination?.total && historyData.pagination.total !== filteredStats.count && (
                              <> ({Math.round((filteredStats.count / historyData.pagination.total) * 100)}% tổng số)</>
                            )}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Fade>
                )}

                {/* Compact Filter Section */}
                <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <FilterListIcon sx={{ color: '#1a237e', mr: 1, fontSize: 20 }} />
                      <Typography variant="subtitle1" sx={{ color: '#1a237e', fontWeight: 600 }}>
                        Bộ lọc
                      </Typography>
                    </Box>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={6} sm={3}>
                        <DatePicker
                          label="Từ ngày"
                          value={filters.startDate}
                          onChange={filterHandlers.startDate}
                          slotProps={{
                            textField: { size: 'small', fullWidth: true }
                          }}
                        />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <DatePicker
                          label="Đến ngày"
                          value={filters.endDate}
                          onChange={filterHandlers.endDate}
                          slotProps={{
                            textField: { size: 'small', fullWidth: true }
                          }}
                        />
                      </Grid>
                      <Grid item xs={6} sm={2}>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          label="Sắp xếp"
                          value={filters.sortBy}
                          onChange={filterHandlers.sortBy}
                        >
                          <MenuItem value="date">Ngày</MenuItem>
                          <MenuItem value="hours">Giờ</MenuItem>
                          <MenuItem value="createdAt">Tạo</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={6} sm={2}>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          label="Thứ tự"
                          value={filters.sortOrder}
                          onChange={filterHandlers.sortOrder}
                        >
                          <MenuItem value="desc">Mới</MenuItem>
                          <MenuItem value="asc">Cũ</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <ButtonGroup size="small" fullWidth variant="outlined">
                          <Button onClick={handleApplyFilters} disabled={loading}>
                            Áp dụng
                          </Button>
                          <Button onClick={handleResetFilters} disabled={loading}>
                            Reset
                          </Button>
                        </ButtonGroup>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Optimized History List */}
                <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                  <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', bgcolor: '#f8f9fa' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle1" sx={{ color: '#1a237e', fontWeight: 600 }}>
                        📋 Danh sách ({historyData.pagination.total || 0} bản ghi)
                      </Typography>
                      <Button
                        onClick={handleRefresh}
                        disabled={loading}
                        startIcon={<RefreshIcon />}
                        size="small"
                        sx={{ textTransform: 'none' }}
                      >
                        Làm mới
                      </Button>
                    </Stack>
                  </Box>

                  {initialLoading || loading ? (
                    <OptimizedSkeleton isMobile={isMobile} count={isMobile ? 5 : 8} />
                  ) : historyData.records.length > 0 ? (
                    <>
                      {renderedContent}
                      <Box sx={{ p: 1, borderTop: '1px solid #e0e0e0' }}>
            <TablePagination
              component="div"
                          count={historyData.pagination.total || 0}
                          rowsPerPage={historyData.pagination.limit || 25}
                          page={(historyData.pagination.page || 1) - 1}
                          onPageChange={handlePageChange}
                          onRowsPerPageChange={handleRowsPerPageChange}
                          rowsPerPageOptions={[15, 25, 50, 100]}
                          labelRowsPerPage=""
                          labelDisplayedRows={({ from, to, count }) => `${from}-${to}/${count}`}
              sx={{
                            '.MuiTablePagination-toolbar': { minHeight: 48 },
                            '.MuiTablePagination-selectLabel': { display: 'none' },
                            '.MuiTablePagination-select': { fontSize: '0.875rem' }
              }}
            />
          </Box>
                    </>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <HistoryIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 1 }} />
                      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                        Không có dữ liệu
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {filters.startDate || filters.endDate 
                          ? 'Không tìm thấy lịch sử với bộ lọc hiện tại.'
                          : 'Bạn chưa có lịch sử chấm công nào.'
                        }
                      </Typography>
                      {(filters.startDate || filters.endDate) && (
                        <Button variant="outlined" onClick={handleResetFilters} size="small">
                          Xóa bộ lọc
                        </Button>
                      )}
                    </Box>
                  )}
                </Card>
              </>
            )}

            {/* Period Summaries Tab */}
            {tabValue === 1 && (
              <>
                {/* Period Summary Statistics */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} sm={3}>
                    <AnimatedStatsCard
                      title="Số kỳ"
                      value={periodSummaries.length}
                      subtitle="Kỳ đã lưu"
                      color="#9c27b0"
                      gradient="linear-gradient(45deg, #9c27b0 30%, #ba68c8 90%)"
                      icon={WorkHistoryIcon}
                      loading={periodLoading}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <AnimatedStatsCard
                      title="Tổng giờ"
                      value={periodSummaries.reduce((sum, p) => sum + p.totalHours, 0)}
                      subtitle="Tất cả các kỳ"
                      color="#4caf50"
                      icon={AccessTimeIcon}
                      loading={periodLoading}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <AnimatedStatsCard
                      title="Thu nhập"
                      value={new Intl.NumberFormat('vi-VN', {
                        style: 'currency',
                        currency: 'VND',
                        maximumFractionDigits: 0
                      }).format(periodSummaries.reduce((sum, p) => sum + (p.grandTotal ?? p.totalEarnings), 0))}
                      subtitle="Tất cả các kỳ"
                      color="#ff9800"
                      icon={MonetizationOnIcon}
                      loading={periodLoading}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <AnimatedStatsCard
                      title="Trung bình"
                      value={periodSummaries.length > 0 
                        ? `${Math.round(periodSummaries.reduce((sum, p) => sum + p.totalHours, 0) / periodSummaries.length)}h` 
                        : '0h'}
                      subtitle="Giờ/kỳ"
                      color="#2196f3"
                      icon={TrendingUpIcon}
                      loading={periodLoading}
                    />
                  </Grid>
                </Grid>

                {/* Period Summaries List */}
                <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                  <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', bgcolor: '#f8f9fa' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle1" sx={{ color: '#1a237e', fontWeight: 600 }}>
                        💰 Thu nhập theo kỳ ({periodSummaries.length} kỳ)
                      </Typography>
                      <Button
                        onClick={() => {
                          dataCache.delete(`periodSummaries_${user.userId}`);
                          fetchPeriodSummaries();
                        }}
                        disabled={periodLoading}
                        startIcon={<RefreshIcon />}
                        size="small"
                        sx={{ textTransform: 'none' }}
                      >
                        Làm mới
                      </Button>
                    </Stack>
                  </Box>

                  {periodLoading ? (
                    <Box sx={{ p: 2 }}>
                      <Stack spacing={2}>
                        {Array.from({ length: 3 }, (_, index) => (
                          <Skeleton key={index} variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
                        ))}
                      </Stack>
                    </Box>
                  ) : periodSummaries.length > 0 ? (
                    <Box sx={{ p: 2 }}>
                      <Stack spacing={3}>
                        {periodSummaries.map((summary, index) => (
                          <PeriodSummaryCard 
                            key={summary._id} 
                            summary={summary} 
                            index={index}
                          />
                        ))}
                      </Stack>
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <WorkHistoryIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 1 }} />
                      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                        Chưa có lịch sử thu nhập theo kỳ
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Thu nhập sẽ được tự động lưu vào ngày 10 hàng tháng (cuối kỳ).
                      </Typography>
                      <Alert severity="info" sx={{ mt: 2, maxWidth: 400, mx: 'auto' }}>
                        <Typography variant="body2">
                          <strong>Chu kỳ tính lương:</strong> Từ ngày 10 tháng trước đến ngày 9 tháng này.
                          Hệ thống sẽ tự động lưu thu nhập khi hết kỳ.
                        </Typography>
                      </Alert>
                    </Box>
                  )}
        </Card>
              </>
            )}

            {/* Commission History Tab */}
            {tabValue === 2 && (
              <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                <Box sx={{ p: { xs: 2, md: 3 }, borderBottom: '1px solid #e2e8f0', bgcolor: '#fffbeb' }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <DiamondIcon sx={{ color: '#d97706', fontSize: 24 }} />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#92400e' }}>
                        Lịch sử hoa hồng
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Hoa hồng theo % trên doanh thu livestream đã duyệt
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                {commLoading ? (
                  <Box sx={{ p: 3 }}>
                    <Stack spacing={1}>
                      {[1,2,3].map(i => <Skeleton key={i} variant="rectangular" height={48} sx={{ borderRadius: 1 }} />)}
                    </Stack>
                  </Box>
                ) : commHistory.length > 0 ? (
                  <>
                    {/* Summary cards */}
                    <Box sx={{ p: { xs: 2, md: 3 } }}>
                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={4}>
                          <Paper elevation={0} sx={{ p: 2, borderRadius: 2, textAlign: 'center', bgcolor: '#eff6ff', border: '1px solid #bfdbfe' }}>
                            <Typography variant="caption" sx={{ color: '#3b82f6', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem' }}>
                              Tổng doanh thu
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e40af' }}>
                              {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 }).format(
                                commHistory.reduce((s, c) => s + (c.totalGMV || 0), 0)
                              )}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Paper elevation={0} sx={{ p: 2, borderRadius: 2, textAlign: 'center', bgcolor: '#ecfdf5', border: '1px solid #a7f3d0' }}>
                            <Typography variant="caption" sx={{ color: '#059669', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem' }}>
                              Tổng hoa hồng
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#065f46' }}>
                              {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 }).format(
                                commHistory.reduce((s, c) => s + (c.commissionAmount || 0), 0)
                              )}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <Paper elevation={0} sx={{ p: 2, borderRadius: 2, textAlign: 'center', bgcolor: '#fef3c7', border: '1px solid #fde68a' }}>
                            <Typography variant="caption" sx={{ color: '#d97706', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem' }}>
                              Số tháng
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#92400e' }}>
                              {commHistory.filter(c => c.totalGMV > 0).length} tháng
                            </Typography>
                          </Paper>
                        </Grid>
                      </Grid>

                      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
                        <Table size={isMobile ? 'small' : 'medium'}>
                          <TableHead>
                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                              <TableCell sx={{ fontWeight: 700 }}>Tháng</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>Doanh thu</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 700 }}>% Hoa hồng</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>Tiền hoa hồng</TableCell>
                              {!isMobile && <TableCell align="center" sx={{ fontWeight: 700 }}>Trạng thái</TableCell>}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {commHistory.map((item) => {
                              const hasBreakdown = item.revenueBreakdown && item.revenueBreakdown.length > 0;
                              const isExpanded = expandedCommMonth === item.monthKey;
                              return (
                                <React.Fragment key={item.monthKey}>
                                  <TableRow
                                    hover
                                    sx={{ '&:hover': { bgcolor: '#fffbeb' }, cursor: hasBreakdown ? 'pointer' : 'default' }}
                                    onClick={() => hasBreakdown && setExpandedCommMonth(isExpanded ? null : item.monthKey)}
                                  >
                                    <TableCell>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {hasBreakdown && (
                                          <IconButton size="small" sx={{ p: 0, mr: 0.5 }}>
                                            {isExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                                          </IconButton>
                                        )}
                                        <Typography variant="body2" fontWeight={600}>{item.monthKey}</Typography>
                                      </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography variant="body2" fontWeight={600} sx={{ color: '#1e40af' }}>
                                        {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 }).format(item.totalGMV || 0)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                        <Chip
                                          label={`${item.commissionPercent || 0}%`}
                                          size="small"
                                          sx={{
                                            fontWeight: 700,
                                            bgcolor: (item.commissionPercent || 0) > 0 ? '#fef3c7' : '#f1f5f9',
                                            color: (item.commissionPercent || 0) > 0 ? '#92400e' : '#94a3b8'
                                          }}
                                        />
                                        {item.isTimeBased && (
                                          <Chip
                                            icon={<AccessTimeIcon sx={{ fontSize: 12 }} />}
                                            label="Theo giờ"
                                            size="small"
                                            sx={{
                                              height: 18,
                                              fontSize: '0.55rem',
                                              bgcolor: '#dbeafe',
                                              color: '#1e40af',
                                              '& .MuiChip-icon': { color: '#1e40af' },
                                              '& .MuiChip-label': { px: 0.5 }
                                            }}
                                          />
                                        )}
                                      </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography variant="body2" fontWeight={700} sx={{ color: '#065f46' }}>
                                        {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 }).format(item.commissionAmount || 0)}
                                      </Typography>
                                    </TableCell>
                                    {!isMobile && (
                                      <TableCell align="center">
                                        {item.isSnapshot ? (
                                          <Chip label="Đã chốt" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                                        ) : (
                                          <Chip label={item.note || 'Tạm tính'} size="small" variant="outlined" sx={{ fontSize: '0.7rem', color: '#d97706', borderColor: '#fde68a' }} />
                                        )}
                                      </TableCell>
                                    )}
                                  </TableRow>

                                  {/* Expanded breakdown row */}
                                  {hasBreakdown && (
                                    <TableRow>
                                      <TableCell colSpan={isMobile ? 4 : 5} sx={{ py: 0, px: 0, border: isExpanded ? undefined : 'none' }}>
                                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                          <Box sx={{ py: 1.5, px: { xs: 1.5, md: 3 }, bgcolor: '#fefce8' }}>
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#92400e', fontSize: '0.7rem', mb: 1, display: 'block' }}>
                                              Chi tiết từng phiên ({item.revenueBreakdown.length} phiên)
                                            </Typography>
                                            <Stack spacing={1}>
                                              {item.revenueBreakdown.map((rb, idx) => (
                                                <Paper key={idx} elevation={0} sx={{ p: 1.5, borderRadius: 2, border: '1px solid #fde68a', bgcolor: 'white' }}>
                                                  <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={0.5}>
                                                    <Box>
                                                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e40af', fontSize: '0.82rem' }}>
                                                        Doanh thu: {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 }).format(rb.totalSales || 0)}
                                                      </Typography>
                                                      {rb.isTimeBased && rb.breakdown && rb.breakdown.length > 0 ? (
                                                        <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                                                          {rb.breakdown.map((b, bi) => (
                                                            <Chip
                                                              key={bi}
                                                              icon={<AccessTimeIcon sx={{ fontSize: 12 }} />}
                                                              label={`${b.startTime}–${b.endTime} [${b.slotName}] ${b.rate}%: ${new Intl.NumberFormat('vi-VN').format(b.commission || 0)}đ`}
                                                              size="small"
                                                              variant="outlined"
                                                              sx={{
                                                                height: 22, fontSize: '0.65rem',
                                                                borderColor: '#fde68a', color: '#92400e',
                                                                '& .MuiChip-icon': { color: '#d97706' },
                                                                '& .MuiChip-label': { px: 0.75 }
                                                              }}
                                                            />
                                                          ))}
                                                        </Stack>
                                                      ) : (
                                                        <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.7rem' }}>
                                                          Flat rate {rb.effectiveRate || 0}%
                                                        </Typography>
                                                      )}
                                                    </Box>
                                                    <Box sx={{ textAlign: 'right' }}>
                                                      <Chip
                                                        label={`${rb.effectiveRate || 0}%`}
                                                        size="small"
                                                        sx={{ fontWeight: 700, bgcolor: '#fef3c7', color: '#92400e', fontSize: '0.7rem', mb: 0.25 }}
                                                      />
                                                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#065f46', fontSize: '0.85rem' }}>
                                                        {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 }).format(rb.commissionAmount || 0)}
                                                      </Typography>
                                                    </Box>
                                                  </Stack>
                                                </Paper>
                                              ))}
                                            </Stack>
                                          </Box>
                                        </Collapse>
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <DiamondIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 1 }} />
                    <Typography variant="subtitle1" color="text.secondary">
                      Chưa có dữ liệu hoa hồng
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Hoa hồng sẽ được tính khi có doanh thu livestream được duyệt.
                    </Typography>
                  </Box>
                )}
              </Card>
            )}

            {/* Optimized Notification */}
            <Snackbar
              open={notification.open}
              autoHideDuration={4000}
              onClose={handleCloseNotification}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Alert 
                onClose={handleCloseNotification} 
                severity={notification.severity}
                variant="filled"
                sx={{ width: '100%', fontSize: '0.875rem' }}
              >
                {notification.message}
              </Alert>
            </Snackbar>
      </Container>
    </Sidebar>
      </Suspense>
    </LocalizationProvider>
  );
};

export default History; 